import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { Store } from '@ngrx/store';
import { IStateService } from 'angular-ui-router';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ISdcStore } from '../../../../app/sdc/flux/sdc-store.interface';
import {
  CreditsRoute,
  SettingsRoute,
  SettingsRoutesStates,
  SupportsRoute,
} from '../../../shared/constants/settings-routes.constant';
import { ChangeSavedInMetrics } from '../../../store/actions/metrics-actions';
import { getMetricsState } from '../../../store/selectors/metrics-selector';
import { ImSchool } from '../../services/im-models/im-school';
import { UtilitiesService } from '../../services/utilities/utilities.service';
import { IStudentSupport } from '../../typings//interfaces/student-support.interface';
import { IActivity } from '../../typings/interfaces/activity.interface';
import { IAttendanceRecord } from '../../typings/interfaces/attendance-record.interface';
import { ISchoolAssessment } from '../../typings/interfaces/school-assessment.interface';
import { ISchoolSupport } from '../../typings/interfaces/support.interface';
import { IUser } from '../../typings/interfaces/user.interface';

@Component({
  selector: 'settings',
  template: require('./settings.component.html'),
  styles: [require('./settings.component.scss')],
})
export class SettingsComponent implements OnInit {
  public CreditsRoute = CreditsRoute;
  public SettingsRoute = SettingsRoute;
  public SupportsRoute = SupportsRoute;

  @ViewChild('textContainer') textContainer: ElementRef;
  @Input() currentUser: IUser;
  @Input() settingsRoute: SettingsRoute;
  @Input() activities$: Observable<IActivity[]>;
  @Input() schoolAssessments$: Observable<ISchoolAssessment[]>;
  @Input() attendanceRecords: IAttendanceRecord[];
  @Input() creditsRoute: CreditsRoute;
  @Input() currentSchool: any;
  @Input() currentSupports;
  @Input() gradPlanRoute: string;
  @Input() schoolUsers: any;
  @Input() studentSupports$: Observable<IStudentSupport[]>;
  @Input() supportsRoute: SupportsRoute;
  @ViewChild('gradPlanningTabs') gradPlanningTabs: MatTabGroup;
  @ViewChild('metricsTabs') metricsTabs: MatTabGroup;
  @ViewChild('supportsTabs') supportsTabs: MatTabGroup;

  public isTransferSchool: boolean = false;
  private isPbatSchool: boolean = false;
  public animationDuration: string = '0ms';
  public displayBoxShadow: boolean = false;
  public schoolSupports: ISchoolSupport[];
  public selectedGradPlanIndex: number = 0;
  public selectedMetricIndex: number = 0;
  public selectedSupportIndex: number = 0;
  public metricsTabNames = {
    creditMetrics: 'CREDIT METRICS',
    regentsMetrics: 'REGENTS METRICS',
    gradPlanningMetrics: 'GRAD PLANNING METRICS',
  };
  public gradPlanningTabNames = {
    firstYear: '1ST YEAR STUDENTS',
    secondYear: '2ND YEAR STUDENTS',
    thirdYear: '3RD YEAR STUDENTS',
    fourthYear: '4TH YEAR STUDENTS',
  };
  public supportTabNames = {
    allSupports: 'ALL SUPPORTS',
    supportRosters: 'SUPPORTS ROSTERS',
    supportAttendance: 'SUPPORT ATTENDANCE',
  };

  constructor(
    @Inject('$state') private $state: IStateService,
    @Inject('SDCStore') private SDCStore: ISdcStore,
    @Inject('Support') private Support,
    private imSchool: ImSchool,
    private store: Store<any>,
    private utilitiesService: UtilitiesService,
  ) {
    // empty block
  }

  ngOnInit(): void {
    this.isTransferSchool = this.imSchool.isTransferSchool(this.currentSchool);
    this.isPbatSchool = this.imSchool.isPbatSchool(this.currentSchool);
    if (this.settingsRoute === SettingsRoute.Metrics) {
      this.setMetricsTab();
    } else if (this.settingsRoute === SettingsRoute.Supports) {
      this.schoolSupports = this.Support.getNotDeletedSupports(this.SDCStore.getAllSchoolSupports());
      this.setSupportsTab();
    }
  }

  ngAfterViewInit(): void {
    if (this.metricsTabs) this.metricsTabs._handleClick = this.interceptTabChange.bind(this);
    if (this.gradPlanningTabs) this.gradPlanningTabs._handleClick = this.interceptTabChange.bind(this);
    if (this.supportsTabs) this.supportsTabs._handleClick = this.interceptSupportTabChange.bind(this);
  }

  public onWindowScroll(): void {
    const scrollTop = this.textContainer.nativeElement.scrollTop;
    this.displayBoxShadow = this.utilitiesService.showListPanelShadow(scrollTop);
  }

  private setMetricsTab(): void {
    switch (this.creditsRoute) {
      case CreditsRoute.Credits: {
        this.selectedMetricIndex = 0;
        break;
      }
      case CreditsRoute.Regents: {
        this.selectedMetricIndex = 1;
        break;
      }
      case CreditsRoute.GradPlanning: {
        this.setGradPlanTab();
        this.selectedMetricIndex = 2;
        break;
      }
      default: {
        this.selectedMetricIndex = 0;
        break;
      }
    }
  }

  private setGradPlanTab(): void {
    switch (this.gradPlanRoute) {
      case 'first': {
        this.selectedGradPlanIndex = 3;
        break;
      }
      case 'second': {
        this.selectedGradPlanIndex = 2;
        break;
      }
      case 'third': {
        this.selectedGradPlanIndex = 1;
        break;
      }
      case 'fourth': {
        this.selectedGradPlanIndex = 0;
        break;
      }
      default: {
        this.selectedGradPlanIndex = 0;
        break;
      }
    }
  }

  public goToMetricsRoute(tabName): void {
    switch (tabName) {
      case this.metricsTabNames.creditMetrics: {
        let route;
        if (this.isTransferSchool) {
          route = 'main.school.settings.metrics.distance-to-grad';
        } else {
          route = 'main.school.settings.metrics.credits';
        }
        this.$state.go(route);
        break;
      }
      case this.metricsTabNames.regentsMetrics: {
        this.$state.go('main.school.settings.metrics.regents');
        break;
      }
      case this.metricsTabNames.gradPlanningMetrics: {
        this.$state.go('main.school.settings.metrics.grad-planning.fourth-year-students');
        break;
      }
      default: {
        this.$state.go('main.school.settings.metrics.credits');
        break;
      }
    }
  }

  public goToGradPlanRoute(tabName): void {
    switch (tabName) {
      case this.gradPlanningTabNames.fourthYear: {
        this.$state.go('main.school.settings.metrics.grad-planning.fourth-year-students');
        break;
      }
      case this.gradPlanningTabNames.thirdYear: {
        this.$state.go('main.school.settings.metrics.grad-planning.third-year-students');
        break;
      }
      case this.gradPlanningTabNames.secondYear: {
        this.$state.go('main.school.settings.metrics.grad-planning.second-year-students');
        break;
      }
      case this.gradPlanningTabNames.firstYear: {
        this.$state.go('main.school.settings.metrics.grad-planning.first-year-students');
        break;
      }
      default: {
        this.$state.go('main.school.settings.metrics.grad-planning.fourth-year-students');
        break;
      }
    }
  }

  private setSupportsTab(): void {
    switch (this.supportsRoute) {
      case SupportsRoute.AllSupports: {
        this.selectedSupportIndex = 0;
        break;
      }
      case SupportsRoute.SupportRosters: {
        this.selectedSupportIndex = 1;
        break;
      }
      case SupportsRoute.SupportAttendance: {
        this.selectedSupportIndex = 2;
        break;
      }
      default: {
        this.selectedSupportIndex = 0;
        break;
      }
    }
  }

  public goToSupportsRoute(tabName: string): void {
    switch (tabName) {
      case this.supportTabNames.allSupports: {
        this.$state.go('main.school.settings.supports.all-supports');
        break;
      }
      case this.supportTabNames.supportRosters: {
        this.$state.go('main.school.settings.supports.supports-roster');
        break;
      }
      case this.supportTabNames.supportAttendance: {
        this.$state.go('main.school.settings.supports.support-attendance');
        break;
      }
      default: {
        this.$state.go('main.school.settings.supports.all-supports');
        break;
      }
    }
  }

  public changeView(route: string): void {
    if (route !== this.settingsRoute) {
      if (route === SettingsRoute.Metrics) {
        if (this.isTransferSchool) {
          this.$state.go('main.school.settings.metrics.distance-to-grad');
        } else {
          this.$state.go(SettingsRoutesStates[route]);
        }
      } else {
        this.$state.go(SettingsRoutesStates[route]);
      }
    }
  }

  private interceptTabChange(tab: MatTab): void {
    const message = 'You have unsaved changes, Are you sure you want to leave this page?';
    const tabTitle = tab.textLabel;
    const tabTitleLower = tabTitle.toLowerCase();
    let result;
    if (tabTitleLower.includes('year students')) {
      if (this.currentSchool.hasChanges()) {
        result = confirm(message);
        if (result) {
          this.store.dispatch(new ChangeSavedInMetrics());
          this.goToGradPlanRoute(tabTitle);
        }
      } else {
        this.goToGradPlanRoute(tabTitle);
      }
    } else if (this.selectedMetricIndex === 2) {
      if (this.currentSchool.hasChanges()) {
        result = confirm(message);
        if (result) {
          this.store.dispatch(new ChangeSavedInMetrics());
          this.goToMetricsRoute(tabTitle);
        }
      } else {
        this.goToMetricsRoute(tabTitle);
      }
    } else {
      const metricsSlice = this.store.select(getMetricsState);
      metricsSlice.pipe(take(1)).subscribe(({ changeMade }) => {
        if (changeMade) {
          result = confirm(message);
          if (result) {
            this.store.dispatch(new ChangeSavedInMetrics());
            this.goToMetricsRoute(tabTitle);
          }
        } else {
          this.goToMetricsRoute(tabTitle);
        }
      });
    }
  }

  private interceptSupportTabChange(tab: MatTab): void {
    this.goToSupportsRoute(tab.textLabel);
  }
}
